import React, { useState } from 'react';
import { Button, Form, Icon, Table } from 'semantic-ui-react';
import { ClientObservationEntryDto } from '../../services/contracts/ContractService';
import { clientService } from '../../services/clients/ClientService';
import { useToast } from '../../hooks/useToast';
import { formatHubUtc } from '../contracts/clientContractHubUtils';

export interface ClientObservationsPanelProps {
  clientId: string;
  observations: ClientObservationEntryDto[];
  canAdd?: boolean;
  contractId?: string;
  sourceType?: string;
  onObservationsChanged: () => void | Promise<void>;
  compact?: boolean;
  /** Quando true, não repete a tabela (use com ClientObservationsSummary acima). */
  hideTable?: boolean;
}

const formatObservationDate = (value?: string) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} - ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const ClientObservationsPanel: React.FC<ClientObservationsPanelProps> = ({
  clientId,
  observations,
  canAdd = false,
  contractId,
  sourceType = 'manual',
  onObservationsChanged,
  compact = false,
  hideTable = false,
}) => {
  const { showSuccess, showError } = useToast();
  const [isAdding, setIsAdding] = useState(false);
  const [occurrenceDraft, setOccurrenceDraft] = useState('');
  const [observationDraft, setObservationDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setOccurrenceDraft('');
    setObservationDraft('');
    setIsAdding(false);
  };

  const handleSave = async () => {
    const occurrence = occurrenceDraft.trim();
    const observation = observationDraft.trim();
    if (!occurrence && !observation) {
      showError('Informe a ocorrência ou a observação.');
      return;
    }

    try {
      setSaving(true);
      await clientService.addClientObservation(clientId, {
        clientId,
        occurrence: occurrence || undefined,
        observation: observation || undefined,
        contractId,
        sourceType,
      });
      showSuccess('Observação registrada no histórico do cliente.');
      resetForm();
      await onObservationsChanged();
    } catch (err: unknown) {
      const apiMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      showError(apiMessage || (err instanceof Error ? err.message : 'Erro ao salvar observação.'));
    } finally {
      setSaving(false);
    }
  };

  const textareaRows = compact ? 5 : 6;
  const tableMaxHeight = compact ? 280 : 420;

  return (
    <div>
      {!hideTable && observations.length === 0 && !isAdding ? (
        <div style={{ textAlign: 'center', padding: compact ? '16px 0' : '24px 0', color: '#aaa' }}>
          <Icon name="comment outline" style={{ fontSize: '1.8rem', opacity: 0.4 }} />
          <p style={{ margin: '8px 0 0', fontSize: '0.9rem' }}>
            Nenhuma observação interna cadastrada.
          </p>
          {canAdd && (
            <Button
              size="small"
              style={{
                marginTop: 12,
                background: '#8e44ad',
                color: 'white',
                borderRadius: 20,
                border: 'none',
              }}
              onClick={() => setIsAdding(true)}
            >
              <Icon name="plus" />
              Adicionar primeira observação
            </Button>
          )}
        </div>
      ) : !hideTable ? (
        <div
          style={{
            border: '1px solid #dee2e6',
            borderRadius: 8,
            overflow: 'auto',
            maxHeight: tableMaxHeight,
            marginBottom: isAdding ? 14 : 0,
          }}
        >
          <Table celled striped compact size="small" style={{ margin: 0, fontSize: '0.88rem' }}>
            <Table.Header>
              <Table.Row style={{ background: '#f1f3f5' }}>
                <Table.HeaderCell width={3}>Data</Table.HeaderCell>
                <Table.HeaderCell width={4}>Colaborador</Table.HeaderCell>
                <Table.HeaderCell width={4}>Ocorrência</Table.HeaderCell>
                <Table.HeaderCell width={5}>Observações</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {observations.map((entry) => (
                <Table.Row key={entry.id}>
                  <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                    {formatObservationDate(entry.occurredAtUtc) || formatHubUtc(entry.occurredAtUtc)}
                  </Table.Cell>
                  <Table.Cell style={{ textTransform: 'uppercase', fontSize: '0.82rem' }}>
                    {entry.collaboratorName || '—'}
                  </Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'pre-wrap' }}>{entry.occurrence || '—'}</Table.Cell>
                  <Table.Cell style={{ whiteSpace: 'pre-wrap' }}>{entry.observation || '—'}</Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      ) : null}

      {hideTable && canAdd && !isAdding && (
        <Button
          size="mini"
          style={{
            marginTop: observations.length > 0 ? 8 : 0,
            background: '#8e44ad',
            color: 'white',
            borderRadius: 20,
            border: 'none',
          }}
          onClick={() => setIsAdding(true)}
        >
          <Icon name="plus" />
          Registrar observação
        </Button>
      )}

      {canAdd && !isAdding && !hideTable && observations.length > 0 && (
        <Button
          size="small"
          style={{
            marginTop: 12,
            background: '#8e44ad',
            color: 'white',
            borderRadius: 20,
            border: 'none',
          }}
          onClick={() => setIsAdding(true)}
        >
          <Icon name="plus" />
          Nova observação
        </Button>
      )}

      {isAdding && (
        <div
          style={{
            marginTop: observations.length > 0 ? 14 : 0,
            background: '#f8f4ff',
            border: '1px dashed #ce93d8',
            borderRadius: 10,
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
            <Icon name="plus circle" style={{ color: '#8e44ad', margin: 0 }} />
            <span style={{ fontWeight: 600, color: '#6a1b9a', fontSize: '0.85rem' }}>
              Nova observação
            </span>
          </div>
          <Form>
            <Form.Field>
              <label>Ocorrência</label>
              <Form.Input
                value={occurrenceDraft}
                onChange={(_, data) => setOccurrenceDraft(String(data.value ?? ''))}
                placeholder="Ex.: Conferência de linhas, criação de contrato, follow-up comercial..."
              />
            </Form.Field>
            <Form.Field>
              <label>Observação (opcional)</label>
              <Form.TextArea
                value={observationDraft}
                onChange={(_, data) => setObservationDraft(String(data.value ?? ''))}
                rows={textareaRows}
                style={{ width: '100%', minHeight: compact ? 120 : 160, resize: 'vertical' }}
                placeholder="Detalhes adicionais sobre a ocorrência..."
              />
            </Form.Field>
          </Form>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <Button
              size="small"
              loading={saving}
              disabled={saving}
              style={{
                background: '#8e44ad',
                color: 'white',
                borderRadius: 20,
                border: 'none',
              }}
              onClick={() => void handleSave()}
            >
              <Icon name="save" />
              Salvar observação
            </Button>
            <Button
              size="small"
              disabled={saving}
              style={{ borderRadius: 20 }}
              onClick={resetForm}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientObservationsPanel;
